import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Save, Calendar, Shuffle, Edit3, Trash2, Trophy, 
  Users, Target, Clock, CheckCircle, XCircle, PlayCircle, Filter, ArrowUpDown
} from 'lucide-react';
import { doc, getDoc, updateDoc, collection, addDoc, deleteDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';

const FixtureGeneratorPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFixture, setEditingFixture] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  useEffect(() => {
    const fetchLeague = async () => {
      try {
        const leagueDoc = await getDoc(doc(db, 'leagues', leagueId));
        if (leagueDoc.exists()) {
          setLeague({ id: leagueDoc.id, ...leagueDoc.data() });
        } else {
          setError('League not found');
        }
      } catch (err) {
        setError('Failed to load league details');
        console.error('Error fetching league:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeague();
  }, [leagueId]);

  useEffect(() => {
    const teamsQuery = query(
      collection(db, 'teams'),
      orderBy('name', 'asc')
    );

    const unsubscribeTeams = onSnapshot(teamsQuery, (snapshot) => {
      const teamsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTeams(teamsData);
    });

    return unsubscribeTeams;
  }, []);

  useEffect(() => {
    const fixturesQuery = query(
      collection(db, 'fixtures'),
      orderBy('date', 'asc')
    );

    const unsubscribeFixtures = onSnapshot(fixturesQuery, (snapshot) => {
      const fixturesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFixtures(fixturesData.filter(f => f.leagueId === leagueId));
    });

    return unsubscribeFixtures;
  }, [leagueId]);

  const generateRoundRobin = () => {
    if (teams.length < 2) {
      setError('Need at least 2 teams to generate fixtures');
      return;
    }

    const teamList = [...teams];
    const fixtures = [];
    const totalRounds = teamList.length - 1;
    const matchesPerRound = Math.floor(teamList.length / 2);

    // Round-robin algorithm
    for (let round = 0; round < totalRounds; round++) {
      for (let match = 0; match < matchesPerRound; match++) {
        const home = teamList[match];
        const away = teamList[teamList.length - 1 - match];
        
        if (home && away) {
          const fixtureDate = new Date(league.startDate);
          fixtureDate.setDate(fixtureDate.getDate() + (round * 7)); // Weekly matches
          
          // Create 4 matches for each team pairing with different player combinations
          const baseFixture = {
            leagueId,
            round: round + 1,
            homeTeam: home.name,
            homeTeamId: home.id,
            awayTeam: away.name,
            awayTeamId: away.id,
            date: fixtureDate.toISOString().split('T')[0],
            time: '18:00',
            court: `Court ${(match % league.matchRules.courtCount) + 1}`,
            status: 'scheduled',
            createdAt: new Date().toISOString()
          };

          // Match 1-4: Different player pairings
          for (let i = 1; i <= 4; i++) {
            fixtures.push({
              ...baseFixture,
              matchNumber: fixtures.length + 1,
              matchType: `Match ${i}`,
              gameNumber: i
            });
          }
        }
      }
      
      // Rotate teams (except first team)
      const lastTeam = teamList.pop();
      teamList.splice(1, 0, lastTeam);
    }

    return fixtures;
  };

  const handleGenerateFixtures = async () => {
    setSaving(true);
    setError('');

    try {
      // Delete existing fixtures for this league
      const existingFixtures = fixtures.filter(f => f.leagueId === leagueId);
      for (const fixture of existingFixtures) {
        await deleteDoc(doc(db, 'fixtures', fixture.id));
      }

      // Generate new fixtures
      const newFixtures = generateRoundRobin();
      
      // Save fixtures to Firebase
      for (const fixture of newFixtures) {
        await addDoc(collection(db, 'fixtures'), fixture);
      }

      // Update league status
      await updateDoc(doc(db, 'leagues', leagueId), {
        fixturesGenerated: true,
        updatedAt: new Date().toISOString()
      });

    } catch (error) {
      setError('Failed to generate fixtures');
      console.error('Error generating fixtures:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditFixture = (fixture) => {
    setEditingFixture(fixture);
    setShowEditModal(true);
  };

  const handleUpdateFixture = async (updatedFixture) => {
    try {
      await updateDoc(doc(db, 'fixtures', updatedFixture.id), {
        ...updatedFixture,
        updatedAt: new Date().toISOString()
      });
      setShowEditModal(false);
      setEditingFixture(null);
    } catch (error) {
      setError('Failed to update fixture');
      console.error('Error updating fixture:', error);
    }
  };

  const handleDeleteFixture = async (fixtureId) => {
    if (!window.confirm('Are you sure you want to delete this fixture?')) return;
    
    try {
      await deleteDoc(doc(db, 'fixtures', fixtureId));
    } catch (error) {
      setError('Failed to delete fixture');
      console.error('Error deleting fixture:', error);
    }
  };

  const getFilteredFixtures = () => {
    let filtered = [...fixtures];
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(f => f.status === filterStatus);
    }
    
    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(a.date) - new Date(b.date);
      } else if (sortBy === 'round') {
        return (a.round || 0) - (b.round || 0);
      }
      return 0;
    });
    
    return filtered;
  };

  const getFixtureStats = () => {
    const total = fixtures.length;
    const scheduled = fixtures.filter(f => f.status === 'scheduled').length;
    const live = fixtures.filter(f => f.status === 'live').length;
    const completed = fixtures.filter(f => f.status === 'completed').length;
    const cancelled = fixtures.filter(f => f.status === 'cancelled').length;
    
    return { total, scheduled, live, completed, cancelled };
  };

  const stats = getFixtureStats();
  const filteredFixtures = getFilteredFixtures();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <Trophy size={48} style={{ margin: '0 auto 1rem', color: '#10B981' }} />
          </motion.div>
          <p style={{ color: '#6B7280', fontWeight: '600' }}>Loading fixtures...</p>
        </div>
      </div>
    );
  }

  if (error && !league) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1C563D' }}>
            {error}
          </h2>
          <button
            onClick={() => navigate('/league')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Back to Leagues
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #F0FDF4 0%, #FFFFFF 50%, #DBEAFE 100%)' }}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: '2rem' }}
        >
          <button
            onClick={() => navigate(`/league/${leagueId}/leaderboard`)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#6B7280',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              transition: 'color 0.2s'
            }}
          >
            <ArrowLeft size={20} />
            Back to Leaderboard
          </button>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1C563D' }}>
                League Fixtures
              </h1>
              <p style={{ color: '#6B7280', fontSize: '1.125rem' }}>
                {league?.name}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGenerateFixtures}
                disabled={saving || teams.length < 2}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.875rem 1.5rem',
                  background: (saving || teams.length < 2) ? '#9CA3AF' : 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.75rem',
                  cursor: (saving || teams.length < 2) ? 'not-allowed' : 'pointer',
                  fontWeight: '700',
                  fontSize: '0.875rem',
                  boxShadow: (saving || teams.length < 2) ? 'none' : '0 4px 12px rgba(28, 86, 61, 0.3)'
                }}
              >
                <Shuffle size={18} />
                {fixtures.length > 0 ? 'Regenerate Fixtures' : 'Generate Fixtures'}
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem', 
            marginBottom: '2rem' 
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
            padding: '1.5rem',
            borderRadius: '1rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #DBEAFE 0%, #93C5FD 100%)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Calendar size={24} style={{ color: '#1E40AF' }} />
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1E40AF' }}>{stats.total}</div>
                <div style={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: '600' }}>Total Fixtures</div>
              </div>
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
            padding: '1.5rem',
            borderRadius: '1rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #FEF3C7 0%, #FCD34D 100%)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Clock size={24} style={{ color: '#92400E' }} />
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#92400E' }}>{stats.scheduled}</div>
                <div style={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: '600' }}>Scheduled</div>
              </div>
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
            padding: '1.5rem',
            borderRadius: '1rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <PlayCircle size={24} style={{ color: '#DC2626' }} />
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#DC2626' }}>{stats.live}</div>
                <div style={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: '600' }}>Live Now</div>
              </div>
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
            padding: '1.5rem',
            borderRadius: '1rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #D1FAE5 0%, #6EE7B7 100%)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircle size={24} style={{ color: '#065F46' }} />
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#065F46' }}>{stats.completed}</div>
                <div style={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: '600' }}>Completed</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '1rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            marginBottom: '2rem',
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Filter size={18} style={{ color: '#6B7280' }} />
            <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Filter:</span>
          </div>
          
          {['all', 'scheduled', 'live', 'completed', 'cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              style={{
                padding: '0.5rem 1rem',
                background: filterStatus === status ? 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)' : 'transparent',
                color: filterStatus === status ? 'white' : '#6B7280',
                border: filterStatus === status ? 'none' : '1px solid #E5E7EB',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                textTransform: 'capitalize'
              }}
            >
              {status}
            </button>
          ))}
        </motion.div>

        {/* Fixtures Table */}
        {filteredFixtures.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{
              background: 'white',
              borderRadius: '1rem',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              overflow: 'hidden'
            }}
          >
            <div style={{ padding: '1.5rem', borderBottom: '2px solid #E5E7EB' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1C563D', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Trophy size={24} />
                Fixtures List
                <span style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '600', 
                  color: '#6B7280',
                  marginLeft: '0.5rem'
                }}>
                  ({filteredFixtures.length} {filteredFixtures.length === 1 ? 'match' : 'matches'})
                </span>
              </h2>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E5E7EB' }}>
                    <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                      Round
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                      Match
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                      Date
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                      Time
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                      Court
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                      Status
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFixtures.map((fixture, index) => (
                    <tr
                      key={fixture.id}
                      style={{
                        borderBottom: '1px solid #E5E7EB',
                        backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#F9FAFB';
                      }}
                    >
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <span style={{
                          background: 'linear-gradient(135deg, #DBEAFE 0%, #93C5FD 100%)',
                          color: '#1E40AF',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          fontWeight: '700'
                        }}>
                          R{fixture.round}
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontWeight: '600', color: '#1F2937', marginBottom: '0.25rem' }}>
                          {fixture.homeTeam} vs {fixture.awayTeam}
                        </div>
                        {fixture.gameNumber && (
                          <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                            Game #{fixture.gameNumber}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', color: '#374151' }}>
                        {new Date(fixture.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', color: '#374151', fontWeight: '600' }}>
                        {fixture.time}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', color: '#374151' }}>
                        {fixture.court}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <span style={{
                          padding: '0.375rem 0.75rem',
                          borderRadius: '0.375rem',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          background: fixture.status === 'scheduled' ? '#DBEAFE' :
                                     fixture.status === 'live' ? '#FEE2E2' :
                                     fixture.status === 'completed' ? '#D1FAE5' :
                                     '#F3F4F6',
                          color: fixture.status === 'scheduled' ? '#1E40AF' :
                                fixture.status === 'live' ? '#DC2626' :
                                fixture.status === 'completed' ? '#065F46' :
                                '#6B7280'
                        }}>
                          {fixture.status}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleEditFixture(fixture)}
                            style={{
                              padding: '0.5rem',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#3B82F6',
                              transition: 'color 0.2s'
                            }}
                            title="Edit fixture"
                            onMouseEnter={(e) => e.currentTarget.style.color = '#2563EB'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#3B82F6'}
                          >
                            <Edit3 size={16} />
                          </button>
                          
                          <button
                            onClick={() => handleDeleteFixture(fixture.id)}
                            style={{
                              padding: '0.5rem',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#EF4444',
                              transition: 'color 0.2s'
                            }}
                            title="Delete fixture"
                            onMouseEnter={(e) => e.currentTarget.style.color = '#DC2626'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#EF4444'}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '4rem 2rem',
              textAlign: 'center',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Calendar size={64} style={{ margin: '0 auto 1rem', color: '#9CA3AF' }} />
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#374151', marginBottom: '0.5rem' }}>
              No Fixtures Yet
            </h3>
            <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
              Click "Generate Fixtures" to create league matches automatically
            </p>
          </motion.div>
        )}

        {/* Edit Fixture Modal */}
        <AnimatePresence>
          {showEditModal && editingFixture && (
            <EditFixtureModal
              fixture={editingFixture}
              onSave={handleUpdateFixture}
              onClose={() => {
                setShowEditModal(false);
                setEditingFixture(null);
              }}
            />
          )}
        </AnimatePresence>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: '#FEE2E2',
              border: '1px solid #FECACA',
              color: '#DC2626',
              padding: '1rem',
              borderRadius: '0.75rem',
              marginTop: '1rem',
              fontWeight: '600'
            }}
          >
            {error}
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Edit Fixture Modal Component
const EditFixtureModal = ({ fixture, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    date: fixture.date,
    time: fixture.time,
    court: fixture.court,
    status: fixture.status
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...fixture, ...formData });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '1.5rem',
          padding: '2rem',
          width: '100%',
          maxWidth: '500px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1C563D', marginBottom: '0.25rem' }}>
              Edit Fixture
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
              {fixture.homeTeam} vs {fixture.awayTeam}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <XCircle size={24} style={{ color: '#6B7280' }} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              Date
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #E5E7EB',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              Time
            </label>
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #E5E7EB',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              Court
            </label>
            <input
              type="text"
              name="court"
              value={formData.court}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #E5E7EB',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #E5E7EB',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="scheduled">Scheduled</option>
              <option value="live">Live</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '0.875rem',
                background: '#F3F4F6',
                color: '#374151',
                border: 'none',
                borderRadius: '0.75rem',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                flex: 1,
                padding: '0.875rem',
                background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem',
                boxShadow: '0 4px 12px rgba(28, 86, 61, 0.3)'
              }}
            >
              Save Changes
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default FixtureGeneratorPage;
